import { HttpClient } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import { DataStateChangeEventArgs } from '@syncfusion/ej2-angular-grids';
import { Observable, throwError, Subject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpHelper } from '../helpers/http-helper';
import { ILLBusinessCategory } from '../models/business-category.model';
import { ICompanyDetail, IContact, IContactCompany, IContactDetail, IEntity, IServerSettings } from '../models/index';
import { AuthenticationService } from './authentication.service';
import { SettingsService } from './settings.service';
import { StorageManagerService } from './storage-manager.service';

@Injectable()
export class CompanyContactService extends Subject<DataStateChangeEventArgs> {
    private action: string;
    private data: any;
    private settings: IServerSettings;
    private apiUrl: string;
    apiResult: any;
    httpHelper: HttpHelper;

    constructor(private http: HttpClient, private settingsService: SettingsService, private authService: AuthenticationService, private storage: StorageManagerService) {
        super();

        // Define an instance of the http helper class that will be used to create httpHeader for api calls
        this.httpHelper = new HttpHelper(http, authService, storage);
    }

    // Get a list of companies for a client
    GetCompanyDashboard(): Observable < ICompanyDetail[] > {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = `ssdpds/v1/Companies/CompanyDashboard`;

        return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
            .pipe(map(this.extractData),
    catchError(this.handleErrors));
    }
    // Get a list of contacts for a client
    GetContactDashboard(): Observable < IContact[] > {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
            this.action = `ssdpds/v1/Contacts/ContactDashboard`;

        return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
            .pipe(map(this.extractData),
    catchError(this.handleErrors));
    }

    // Get Company detail
    getCompanyById(companyRevisionId: string): Observable<ICompanyDetail> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = `ssdpds/v1/Companies/CompanyDetails/${companyRevisionId}`;

        return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
            .pipe(map(this.extractData),
    catchError(this.handleErrors));
    }

    // Get Company detail
    getBusinessOperationQualifierByCompany(companyRevisionId: string): Observable<ICompanyDetail> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = `ssdpds/v1/Companies/BusinessOperationBusinessOperationQualifier/${companyRevisionId}`;

        return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
            .pipe(map(this.extractData),
    catchError(this.handleErrors));
    }

    // Create new Company
    createCompany(company) {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = 'ssdpds/v1/Companies/InsertCompanyAndCompanyRevision';

        return this.http.post(`${this.apiUrl}/${this.action}`, company, { headers: headers })
            .pipe(map(this.extractData),
    catchError(this.handleErrors));
    }

    // Get Company detail
    getContacts(): Observable<IContact> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = 'ssdpds/v1/Contacts/GetLatestAvailableContacts';

        return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
            .pipe(map(this.extractData),
    catchError(this.handleErrors));
    }

    // Validate if a contact has required information for a specific type of company
    ValidateContactAssociation(contact: IContactCompany) {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = 'ssdpds/v1/Contacts/ValidateContactAssociation';

        return this.http.post(`${this.apiUrl}/${this.action}`, contact, { headers: headers })
            .pipe(map(this.extractData),
    catchError(this.handleErrors));
    }

    // Retrieve historical company records
    getCompanyRevisionHistory(companyRevisionId: number): Observable<ICompanyDetail[]> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = `ssdpds/v1/Companies/CompanyRevisionHistory?companyRevisionId=${companyRevisionId}`;

        return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
            .pipe(map(this.extractData),
    catchError(this.handleErrors));
    }

    // Retrieve historical contact records
    getContactRevisionHistory(contactRevisionId: number): Observable<IContactDetail[]> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = `ssdpds/v1/Contacts/ContactRevisionHistory?contactRevisionId=${contactRevisionId}`;

        return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
            .pipe(map(this.extractData),
    catchError(this.handleErrors));
    }

    // Retrieve a list of business categories
    getBusinessCategory(): Observable<IEntity[]> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = 'ssdpds/v1/CommonData/GetBusinessCategoriesByCustomerGuid';

        return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

  // Retrieve a list of business categories
  getBusinessCategories(bIncludeInactiveRecords:boolean): Observable<ILLBusinessCategory[]> {
    const headers = this.httpHelper.createHttpHeaders();
    this.settings = this.settingsService.getSettings();
    this.apiUrl = this.settings.ApiGatewayUrl;
    this.action = `ssdpds/v1/CommonData/BusinessCategories?includeInactiveRecords=${bIncludeInactiveRecords}`;

    return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
        .pipe(map(this.extractData),
          catchError(this.handleErrors));
  }

  // Create new Company
  createBusinessCategory(businessCategory:ILLBusinessCategory, modifiedByUser: string) {
    const headers = this.httpHelper.createHttpHeaders();
    this.settings = this.settingsService.getSettings();
    this.apiUrl = this.settings.ApiGatewayUrl;
    this.action = `ssdpds/v1/CommonData/UpsertBusinessCategories?modifiedByUser=${modifiedByUser}`;

    return this.http.post(`${this.apiUrl}/${this.action}`, businessCategory, { headers: headers })
        .pipe(map(this.extractData),
          catchError(this.handleErrors));
  }

    // Retrieve a list of business categories
    getBusinessOperations(): Observable<IEntity[]> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = 'ssdpds/v1/CommonData/GetBusinessOperations?vocabType=busOper';

        return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Retrieve a list of regions for a country
    getRegionsByCountry(countryCode: string): Observable<IEntity[]> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = `ssdpds/v1/CommonData/GetRegionsByCountryId/${countryCode}`;

        return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Retrieve a list of countries
    getCountries(): Observable<IEntity[]> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = `ssdpds/v1/CommonData/GetAllCountriesAndRegions`;

        return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Create new contact
    createContact(formValue) {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = 'ssdpds/v1/Contacts/InsertContactRevision';

        return this.http.post(`${this.apiUrl}/${this.action}`, formValue, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Get contact detail
    getContactById(contactRevisionId: string): Observable<IContact> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = `ssdpds/v1/Contacts/contactDetails/${contactRevisionId}`;

        return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Get NDC Labeler codes
    getNDCLabelerCodesByCompany(contactRevisionId: string): Observable<string> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.ApiGatewayUrl;
        this.action = `ssdpds/v1/Companies/GetCompanyNDCLabelerCode/${contactRevisionId}`;

        return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // retrieve data successfully
    private extractData(response: Response) {
        if (response.status < 200 || response.status >= 300) {
            throw new Error('Bad response status: ' + response.status);
        }

        const body = response['data'];
        return body || {};
    }

    // Error handling
    private handleErrors(error: any): Observable<any> {
      const GENERIC_ERROR_MSG = 'It looks like something went wrong. Try reloading the page or contacting support if this issue continues.';
      const bDisplayDetailMsg = error && error.error && error.error.isBusinessErrorMessage ? true : isDevMode() ? true : false;
      error.error = error && error.error && error.error.message && bDisplayDetailMsg ? error.error.message : GENERIC_ERROR_MSG;

      return throwError(error.error);
    }

}



